import { parseDate } from "@internationalized/date";
import {
  WHEN_DURATION_PRESETS,
  type WhenDatePart,
  type WhenDurationPreset,
  type WhenSelection,
  type WhereSelection,
  type WhoSelection,
} from "@ui/components/SearchBar/types";
import type { Bbox } from "@/lib/where-bounding-boxes";
import { isRegionSlug, REGION_BBOXES, type WhereFilter } from "@/lib/where-regions";

/**
 * The URL grammar for search state. Two producers share it: the mobile Explore
 * overlay serialises a selection and `router.get`s a section index, and each
 * section page continuously mirrors its own search state into the URL (see
 * `useSearchUrlSync`). Pages parse it back once per mount to rehydrate — the
 * same path serves shared links, browser back/forward (Inertia remounts on
 * popstate), and the overlay hand-off.
 *
 * Values are human-readable on purpose: lists ride as literal comma-joined
 * words in a stable canonical order, defaults are omitted, and malformed input
 * degrades element-wise rather than throwing.
 */

const WHERE = "where";
const PLACE = "place";
const BBOX = "bbox";
const COUNTRY = "country";
const FROM = "from";
const TO = "to";
const WHO_MEMBERS = "who";
export const MARKETPLACE_QUERY_PARAM = "q";

export const WHERE_PARAM_KEYS = [WHERE, PLACE, BBOX, COUNTRY] as const;
export const WHEN_PARAM_KEYS = [FROM, TO] as const;
export const WHO_PARAM_KEY = WHO_MEMBERS;

/** Suggestion id the map pins on the Where pill after a manual pan. */
export const MAP_AREA_SUGGESTION_ID = "map-area";

// ---------------------------------------------------------------------------
// Query-string primitives

export function splitUrl(url: string): { path: string; params: URLSearchParams } {
  const [path = url, query = ""] = url.split("?");
  return { path, params: new URLSearchParams(query) };
}

/** Query-string builder that keeps commas literal — RFC 3986 permits them in a
 *  query, and `URLSearchParams#toString` would uglify every list param to `%2C`. */
export function buildQueryString(entries: ReadonlyArray<readonly [string, string]>): string {
  return entries
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value).replaceAll("%2C", ",")}`)
    .join("&");
}

/** Rebuild `url` with `owned` as the leading params, dropping every `ownedKeys`
 *  entry the caller no longer emits and keeping foreign params (UTM tags, tab
 *  state, modal keys) untouched in their original order. */
export function mergeOwnedParams(
  url: string,
  owned: ReadonlyArray<readonly [string, string]>,
  ownedKeys: readonly string[],
): string {
  const { path, params } = splitUrl(url);
  const ownedSet = new Set<string>(ownedKeys);
  const foreign: [string, string][] = [];
  params.forEach((value, key) => {
    if (!ownedSet.has(key)) foreign.push([key, value]);
  });
  const query = buildQueryString([...owned, ...foreign]);
  return query ? `${path}?${query}` : path;
}

/** Comma-join `values` in the order `canonical` lists them; `undefined` when empty. */
export function serializeCanonicalList<T extends string>(
  values: readonly T[],
  canonical: readonly T[],
): string | undefined {
  if (values.length === 0) return undefined;
  const selected = new Set<T>(values);
  return canonical.filter((value) => selected.has(value)).join(",");
}

/** Parse a comma list, dropping unknown members element-wise (`low,banana` → `["low"]`). */
export function parseCanonicalList<T extends string>(raw: string | null, canonical: readonly T[]): T[] {
  if (!raw) return [];
  const parts = new Set(raw.split(","));
  return canonical.filter((value) => parts.has(value));
}

/** Parse a comma list of positive integers — deduplicated, ascending. */
export function parseIntList(raw: string | null): number[] {
  if (!raw) return [];
  const ids = raw
    .split(",")
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0);
  return [...new Set(ids)].sort((a, b) => a - b);
}

export function serializeBboxParam(bbox: Bbox): string {
  return bbox.join(",");
}

/** `w,s,e,n` with four finite parts; `null` otherwise. */
export function parseBboxParam(raw: string | null): Bbox | null {
  if (!raw) return null;
  const parts = raw.split(",").map(Number);
  if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) return null;
  return [parts[0] as number, parts[1] as number, parts[2] as number, parts[3] as number];
}

// ---------------------------------------------------------------------------
// Where

export interface WhereParams {
  selection: WhereSelection;
  /** `null` when the area is described by its term alone — a term selection can
   *  reach the writer before its framing box resolves, and a country pick
   *  reconstructs from its code. */
  bbox: Bbox | null;
  /** Term narrowing carried alongside the bbox; term-filtering indexes narrow by
   *  it and use the bbox for map framing only. */
  filter?: WhereFilter | null;
}

const COUNTRY_CODE_SHAPE = /^[A-Za-z]{2}$/;

/**
 * `where` carries the display name; `place` is written only when the id alone
 * reconstructs the area (region slugs, `map-area`); `bbox` is written only
 * when it is not derivable from `place`; `country` rides beside the bbox for a
 * country-scale pick, which term-filtering pages narrow by. "Nearby" ships its
 * resolved bbox under the display name alone — a shared link must reproduce the
 * sharer's area, not re-prompt the recipient for geolocation.
 */
export function serializeWhereParams({ selection, bbox, filter }: WhereParams): Record<string, string> {
  const { kind, id, name } = selection;
  if (kind === "suggestion" && isRegionSlug(id)) return { [WHERE]: name, [PLACE]: id };

  // A map-area pill carries no filter (a pan replaces a term selection), so the
  // country param can only come from a destination pick.
  const params: Record<string, string> = { [WHERE]: name };
  if (kind === "suggestion" && id === MAP_AREA_SUGGESTION_ID) params[PLACE] = id;
  if (bbox) params[BBOX] = serializeBboxParam(bbox);
  if (filter?.kind === "country") params[COUNTRY] = filter.code;
  return params;
}

/** Parse Where params from a URL; `null` when absent or malformed. */
export function parseWhereParams(url: string): WhereParams | null {
  const { params } = splitUrl(url);
  const name = params.get(WHERE);
  if (!name) return parseLegacyWhereParams(params);

  const place = params.get(PLACE);
  const explicitBbox = parseBboxParam(params.get(BBOX));

  if (place) {
    if (isRegionSlug(place)) {
      return {
        selection: { kind: "suggestion", id: place, name },
        bbox: explicitBbox ?? REGION_BBOXES[place],
        filter: { kind: "region", slug: place },
      };
    }
    if (place === MAP_AREA_SUGGESTION_ID) {
      if (!explicitBbox) return null;
      return { selection: { kind: "suggestion", id: place, name }, bbox: explicitBbox, filter: null };
    }
    // Unknown place ids fall through: the bbox (when present) still describes
    // the area, so a stale link degrades to a plain named area instead of dying.
  }

  const country = params.get(COUNTRY);
  const filter: WhereFilter | null =
    country && COUNTRY_CODE_SHAPE.test(country) ? { kind: "country", code: country.toUpperCase() } : null;
  // A country pick is filtered by its code, so it reconstructs from the code
  // alone; a plain named area has nothing but its box to describe it.
  if (!explicitBbox && !filter) return null;
  return { selection: { kind: "destination", id: name, name }, bbox: explicitBbox, filter };
}

// ---------------------------------------------------------------------------
// When

// Tagless part grammar — the three shapes are self-distinguishing:
// `2026-03` (month), `2026-03-15` (ISO date), `3-months` (duration preset).
// Year is carried but climate scoring only uses month numbers — it round-trips
// so the desktop pill can rebuild the exact selection.
const MONTH_SHAPE = /^(\d{4})-(\d{1,2})$/;
const DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

function encodeWhenPart(part: WhenDatePart): string {
  if (part.kind === "month") return `${part.year}-${String(part.month).padStart(2, "0")}`;
  if (part.kind === "date") return part.value.toString();
  return part.preset;
}

function decodeWhenPart(raw: string | null): WhenDatePart | null {
  if (!raw) return null;

  const month = MONTH_SHAPE.exec(raw);
  if (month) {
    const year = Number(month[1]);
    const monthNumber = Number(month[2]);
    if (year < 1 || monthNumber < 1 || monthNumber > 12) return null;
    return { kind: "month", year, month: monthNumber };
  }
  if (DATE_SHAPE.test(raw)) {
    try {
      return { kind: "date", value: parseDate(raw) };
    } catch {
      return null;
    }
  }
  if ((WHEN_DURATION_PRESETS as readonly string[]).includes(raw)) {
    return { kind: "duration", preset: raw as WhenDurationPreset };
  }
  return null;
}

/** Build the params for a When selection (omits empty parts). */
export function serializeWhenParams(when: WhenSelection): Record<string, string> {
  const params: Record<string, string> = {};
  if (when.start) params[FROM] = encodeWhenPart(when.start);
  if (when.end) params[TO] = encodeWhenPart(when.end);
  return params;
}

/** Parse a When selection from a URL; `null` when neither part is present/valid. */
export function parseWhenParams(url: string): WhenSelection | null {
  const { params } = splitUrl(url);
  const start = decodeWhenPart(params.get(FROM)) ?? decodeLegacyWhenPart(params.get(LEGACY_WHEN_START));
  const end = decodeWhenPart(params.get(TO)) ?? decodeLegacyWhenPart(params.get(LEGACY_WHEN_END));
  if (!start && !end) return null;
  return { start, end };
}

// ---------------------------------------------------------------------------
// Who / marketplace query

// Who carries the selected family-member ids (`who=1,2,3`); the page reseeds
// them into `useSearchBarWho`, which resolves ids → ages for the filter.
export function serializeWhoParams(who: WhoSelection): Record<string, string> {
  if (who.memberIds.length === 0) return {};
  return { [WHO_MEMBERS]: [...who.memberIds].sort((a, b) => a - b).join(",") };
}

/** Parse a Who selection from a URL; `null` when absent or no valid ids. */
export function parseWhoParams(url: string): WhoSelection | null {
  const memberIds = parseIntList(splitUrl(url).params.get(WHO_MEMBERS));
  if (memberIds.length === 0) return null;
  return { memberIds };
}

/** Parse the marketplace free-text query from a URL; `""` when absent. */
export function parseMarketplaceQuery(url: string): string {
  return splitUrl(url).params.get(MARKETPLACE_QUERY_PARAM) ?? "";
}

// ---------------------------------------------------------------------------
// Legacy v1 grammar (`where_name`/`where_bbox`/`where_kind`/`where_id`,
// `when_start`/`when_end` with `m:`/`d:`/`p:` tags) — read-only fallback so
// pre-migration shared links keep seeding. Remove one release after the v2
// writers ship, once old links have aged out of circulation.

const LEGACY_WHERE_NAME = "where_name";
const LEGACY_WHERE_BBOX = "where_bbox";
const LEGACY_WHERE_KIND = "where_kind";
const LEGACY_WHERE_ID = "where_id";
const LEGACY_WHEN_START = "when_start";
const LEGACY_WHEN_END = "when_end";

export const LEGACY_SEARCH_PARAM_KEYS = [
  LEGACY_WHERE_NAME,
  LEGACY_WHERE_BBOX,
  LEGACY_WHERE_KIND,
  LEGACY_WHERE_ID,
  LEGACY_WHEN_START,
  LEGACY_WHEN_END,
] as const;

function parseLegacyWhereParams(params: URLSearchParams): WhereParams | null {
  const name = params.get(LEGACY_WHERE_NAME);
  const bbox = parseBboxParam(params.get(LEGACY_WHERE_BBOX));
  if (!name || !bbox) return null;

  const id = params.get(LEGACY_WHERE_ID) ?? name;
  const selection: WhereSelection =
    params.get(LEGACY_WHERE_KIND) === "destination"
      ? { kind: "destination", id, name }
      : { kind: "suggestion", id, name };
  return { selection, bbox, filter: null };
}

function decodeLegacyWhenPart(raw: string | null): WhenDatePart | null {
  if (!raw) return null;
  const separator = raw.indexOf(":");
  if (separator === -1) return null;
  const tag = raw.slice(0, separator);
  const rest = raw.slice(separator + 1);

  if (tag === "m") {
    const parts = rest.split("-");
    if (parts.length !== 2) return null;
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    if (!Number.isInteger(year) || year < 1 || !Number.isInteger(month) || month < 1 || month > 12) return null;
    return { kind: "month", year, month };
  }
  if (tag === "d") return decodeWhenPart(rest);
  if (tag === "p" && (WHEN_DURATION_PRESETS as readonly string[]).includes(rest)) {
    return { kind: "duration", preset: rest as WhenDurationPreset };
  }
  return null;
}
